import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Custom hook for language persistence
 * Ensures language selection is remembered across page refreshes and navigation
 *
 * @returns {Object} - { currentLanguage, changeLanguage, availableLanguages }
 */
export const useLanguagePersistence = () => {
  const { i18n } = useTranslation();

  const availableLanguages = [
    { code: "en", name: "English", nativeName: "English" },
    { code: "de", name: "German", nativeName: "Deutsch" },
    { code: "fr", name: "French", nativeName: "Français" },
    { code: "ae", name: "Arabic", nativeName: "العربية" },
  ];

  // Change language and persist to localStorage
  const changeLanguage = useCallback(
    (languageCode) => {
      if (availableLanguages.some((lang) => lang.code === languageCode)) {
        i18n.changeLanguage(languageCode);
        localStorage.setItem("language", languageCode);
      }
    },
    [i18n, availableLanguages],
  );

  // Sync language on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");

    // If a language is saved and different from current, apply it
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Listen for language changes from other components/tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "language" && e.newValue && e.newValue !== i18n.language) {
        i18n.changeLanguage(e.newValue);
      }
    };

    const handleLanguageChanged = (e) => {
      localStorage.setItem("language", e.detail.language);
    };

    // Listen to storage changes from other tabs/windows
    window.addEventListener("storage", handleStorageChange);

    // Listen to custom language changed event
    window.addEventListener("languageChanged", handleLanguageChanged);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  return {
    currentLanguage: i18n.language,
    changeLanguage,
    availableLanguages,
  };
};

export default useLanguagePersistence;
